import {objectFromEntries} from '@benchling/js-utils';
import {produce} from 'immer';
import _, {Dictionary} from 'underscore';

import {assert} from 'Asserts';
import * as context from 'Context';
import {distribution} from 'metrics/Metrics';
import {ProtocolVersion, Selector} from 'notebook/ELNConstants';
import {
  areEvalSnapshotDepsEqual,
  getNoteTableMap,
  isValidEvaluationSnapshot,
} from 'notebook/mediocre/util/TableMapUtil';
import * as StepConstants from 'notebook/protocols/StepConstants';
import {SimpleTableDoc} from 'notebook/simple-tables/useSimpleEvaluation';
import excess from 'notebook/tables/excess';
import {FullTableMap, MultidocFullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import Resolver, {mergeResolutions} from 'notebook/tables/excess/Resolver';
import {runEvaluation} from 'notebook/tables/excess/runEval';
import {hasLoadingCells, revhashCellId} from 'notebook/tables/excess/Util';
import {
  DocumentTableOp,
  EvaluationMetrics,
  isTableOpDiffMap,
  MultidocTableOpMap,
  UpdateCellsTableOp,
} from 'notebook/tables/TableConstants';
import {NoteRuntimeSnapshot, QueryResolution} from 'notebook/tables/TableStoreTransforms';
import {
  getNumberOfTableCellsInTableMap,
  tableCellCountBucket,
} from 'notebook/util/analytics/TableAnalyticsUtils';
import {BioentityNote, EntryNote, Note} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';

import {
  DocumentMapper,
  DocumentMapVisitor,
  DocumentTraverser,
  DocumentTraverseVisitor,
} from './DocumentTraversal';

// TODO(BNCH-13228): Clean up interface with plugins and surface standalone tables
function getStepTableMap(protocolVersion: ProtocolVersion): FullTableMap {
  const tableMap: FullTableMap = {};
  for (const step of protocolVersion.steps) {
    if (step.level === StepConstants.StepLevel.TABLE && step.table) {
      tableMap[step.table.name] = step.table;
    }
  }
  return tableMap;
}

/**
 * Description of an evaluation for the document with the given cid.
 */
export interface TableEvaluation {
  cid: string;
  evaluationSnapshot: OutputTableMap;
  syncResolutions: Array<QueryResolution>;
  asyncResolutions: Array<Promise<QueryResolution>>;
  cellsWithUnresolvedResolutionQueries: Set<string>;
}

/**
 * `Resolver.resolveFromResolutionQuery` is very tricky to stub.
 *
 * It's an instance method, so to stub it in each instance, we need to stub `Resolver.prototype`.
 * But `resolveFromResolutionQuery` doesn't actually exist on the prototype, because it's declared
 * as an arrow function!
 *
 * Instead, we make `DocumentEvaluationUpdater` (DEU) construct `Resolver` in a roundabout way,
 * using a factory method. We can then stub that factory method to set up a stub that we can use.
 */
export const stubbableFunctions = {
  makeResolver: (...args: ConstructorParameters<typeof Resolver>): Resolver => new Resolver(...args),
};

/**
 * Visitor of hierarchical documents that produces a list of the evaluated tables.
 */
export class EvaluationVisitor implements DocumentTraverseVisitor {
  tableEvals: Array<TableEvaluation>;
  fileId: string;
  documentNameToIds: {[documentName: string]: Array<string>};
  multidocTableOp?: MultidocTableOpMap; // It is somewhat awkward that this is defined here, but it was the simplest option during spike.
  private readonly recordEvalMetrics: () => void;

  constructor(
    fileId: string,
    documentNameToIds: {[documentName: string]: Array<string>} = {},
    multidocTableOp?: MultidocTableOpMap,
    recordEvalMetrics?: () => void
  ) {
    this.fileId = fileId;
    this.tableEvals = [];
    this.documentNameToIds = documentNameToIds;
    this.multidocTableOp = multidocTableOp;
    this.recordEvalMetrics = recordEvalMetrics ?? (() => {});
  }

  runEval(
    itemCids: Map<string, string>,
    fullTableMap: MultidocFullTableMap,
    selectors: Map<string, Array<Selector>>,
    noteRuntimeSnapshots: Map<string, NoteRuntimeSnapshot>,
    multidocTableOp?: MultidocTableOpMap
  ): void {
    const tableNameToProvenanceUuidMap: Map<string, Record<PropertyKey, string | null>> = new Map();
    Object.keys(fullTableMap).forEach((docId) => {
      tableNameToProvenanceUuidMap.set(
        docId,
        objectFromEntries(
          Object.entries(fullTableMap[docId]).map(([tableName, table]) => [
            tableName,
            table.provenanceUuid ?? null,
          ])
        )
      );
    });

    this.tableEvals.push(
      ...runEvaluation({
        itemCids,
        fileId: this.fileId,
        fullTableMap,
        selectors,
        noteRuntimeSnapshots,
        tableNameToProvenanceUuidMap,
        documentNameToIds: this.documentNameToIds,
        multidocTableOp,
      }).values()
    );
  }

  overrideDocumentTableOp(fileId: string, tableOp: DocumentTableOp): void {
    if (this.multidocTableOp == null) {
      this.multidocTableOp = {[fileId]: tableOp};
    } else {
      this.multidocTableOp = produce(this.multidocTableOp, (draft) => {
        draft[fileId] = tableOp;
      });
    }
  }

  /**
   * Determines whether evaluation can be skipped for the note. This method has a potential side effect of overriding
   * the document table op for the note if the `forceExcessRevaluate` context is true (deprecated), or the evaluation
   * snapshot is missing or invalid.
   */
  shouldSkipEvaluation(
    fileId: string,
    item: Note,
    oldItem: Note | null,
    noteTableMap: FullTableMap
  ): boolean {
    const {evaluationSnapshot} = item.noteRuntimeSnapshot;
    if (context.get('forceExcessRevaluate', false)) {
      this.overrideDocumentTableOp(fileId, 'eval-document');
      return false;
    } else if (evaluationSnapshot == null) {
      this.overrideDocumentTableOp(fileId, 'eval-document-missing-snapshot');
      return false;
    } else if (!isValidEvaluationSnapshot(evaluationSnapshot, item, this.fileId, noteTableMap)) {
      this.overrideDocumentTableOp(fileId, 'eval-document-invalid-snapshot');
      return false;
    } else if (!this.shouldRenderFromEvaluationSnapshot(fileId, oldItem, item, noteTableMap)) {
      return false;
    }
    return true;
  }

  /**
   * Return whether a note runtime snapshot's evaluation snapshot should be used for rendering.
   *
   * If the evaluation snapshot's underlying dependencies have not changed, then it safe to render from.
   *
   * This method assumes that the item's eval snapshot is present and valid; the null check below is just
   * for typing convenience.
   */
  shouldRenderFromEvaluationSnapshot(
    fileId: string,
    oldItem: Note | null,
    item: Note,
    itemNoteTableMap: FullTableMap
  ): boolean {
    const {evaluationSnapshot} = item.noteRuntimeSnapshot;
    return (
      // On initial render, only evaluate if necessary.
      (!oldItem && evaluationSnapshot != null && !hasLoadingCells(evaluationSnapshot)) ||
      areEvalSnapshotDepsEqual(fileId, oldItem, item, itemNoteTableMap)
    );
  }

  visitNote(item: Note, oldItem: Note | null): void {
    const startTime = performance.now();
    const noteTableMap = getNoteTableMap(this.fileId, item);

    if (this.shouldSkipEvaluation(this.fileId, item, oldItem, noteTableMap)) {
      return;
    }

    this.recordEvalMetrics();

    this.runEval(
      new Map([[this.fileId, item.cid]]),
      {[this.fileId]: noteTableMap},
      new Map([[this.fileId, item.selectors]]),
      new Map([[this.fileId, item.noteRuntimeSnapshot as NoteRuntimeSnapshot]]),
      this.multidocTableOp
    );
    this.recordEvalAnalytics(startTime, this.fileId, getNumberOfTableCellsInTableMap(noteTableMap));
  }

  private canSkipEvalForNotes(
    items: Map<string, Note>,
    oldItems: Map<string, Note> | null,
    noteTableMap: MultidocFullTableMap
  ): boolean {
    if (context.get('forceExcessRevaluate', false)) {
      return false;
    }
    for (const [fileId, note] of items) {
      if (!this.shouldSkipEvaluation(fileId, note, oldItems?.get(fileId) ?? null, noteTableMap[fileId])) {
        return false;
      }
    }
    return true;
  }

  visitNotes(item: Map<string, Note>, oldItem: Map<string, Note> | null): void {
    assert(item.has(this.fileId), 'Expected map of notes to contain edited note.');
    const startTime = performance.now();
    const noteTableMap: MultidocFullTableMap = {};
    const cidsMap = new Map();
    const selectorsMap = new Map();
    const nrsMap = new Map();

    item.forEach((note, docId) => {
      noteTableMap[docId] = getNoteTableMap(docId, note);
      cidsMap.set(docId, note.cid);
      selectorsMap.set(docId, note.selectors);
      nrsMap.set(docId, note.noteRuntimeSnapshot as NoteRuntimeSnapshot);
    });

    if (this.canSkipEvalForNotes(item, oldItem, noteTableMap)) {
      return;
    }

    this.recordEvalMetrics();

    this.runEval(cidsMap, noteTableMap, selectorsMap, nrsMap, this.multidocTableOp);
    const totalCellCount = Object.values(noteTableMap).reduce(
      (sum, tableMap) => sum + getNumberOfTableCellsInTableMap(tableMap),
      0
    );
    this.recordEvalAnalytics(startTime, this.fileId, totalCellCount);
  }

  private recordEvalAnalytics(startTime: DOMHighResTimeStamp, fileId: string, cellCount: number): void {
    distribution('deu_run_eval_entry', performance.now() - startTime, {
      cell_count_bucket: tableCellCountBucket(cellCount),
      doc_type: APIUtil.apiIdToMaybeType(fileId) ?? 'none',
    });
  }

  visitProtocolVersion(item: ProtocolVersion, oldItem: ProtocolVersion | null): void {
    if (areEvalSnapshotDepsEqual(this.fileId, oldItem, item)) {
      return;
    }

    this.recordEvalMetrics();

    const singleDocId = 'PROTOCOL';
    this.runEval(
      new Map([[singleDocId, item.cid]]),
      {[singleDocId]: getStepTableMap(item)},
      new Map([[singleDocId, item.selectors]]),
      new Map([[singleDocId, item.noteRuntimeSnapshot as NoteRuntimeSnapshot]]),
      {[singleDocId]: 'eval-document-unsupported'}
    );
  }

  visitSimpleTableDoc(item: SimpleTableDoc, oldItem: SimpleTableDoc | null): void {
    if (areEvalSnapshotDepsEqual(this.fileId, oldItem, item)) {
      return;
    }

    this.recordEvalMetrics();

    const singleDocId = 'SIMPLE_TABLE_DOC';
    this.runEval(
      new Map([[singleDocId, item.cid]]),
      {[singleDocId]: item.fullTableMap},
      new Map([[singleDocId, item.selectors]]),
      new Map([[singleDocId, item.noteRuntimeSnapshot]]),
      {[singleDocId]: 'eval-document-unsupported'}
    );
  }
}

/**
 * Describes an update to the document with the given cid.
 */
export interface EvalMergeTuple {
  cid: string;
  mergeUpdateInto: (noteRuntimeSnapshot: NoteRuntimeSnapshot) => NoteRuntimeSnapshot;
}

/**
 * Visitor of a hierarchical document that merges in noteRuntimeSnapshot into the appropriate base document.
 */
export class EvalMergeVisitor implements DocumentMapVisitor {
  evalMergeTuples: Dictionary<EvalMergeTuple>;

  constructor(evalMergeTuples: Array<EvalMergeTuple>) {
    this.evalMergeTuples = _.indexBy(evalMergeTuples, 'cid');
  }

  visitItem<T extends {noteRuntimeSnapshot: NoteRuntimeSnapshot; cid: string}>(item: T): T {
    if (!this.evalMergeTuples[item.cid]) {
      return item;
    }
    const {mergeUpdateInto} = this.evalMergeTuples[item.cid];
    return {
      ...item,
      noteRuntimeSnapshot: mergeUpdateInto(item.noteRuntimeSnapshot),
    };
  }

  visitNote<T extends BioentityNote | EntryNote>(item: T): T {
    return this.visitItem(item);
  }

  visitNotes(items: Map<string, EntryNote>): Map<string, EntryNote> {
    const result: Map<string, EntryNote> = new Map();
    items.forEach((note, docId) => {
      result.set(docId, this.visitItem(note));
    });
    return result;
  }

  visitProtocolVersion(item: ProtocolVersion): ProtocolVersion {
    return this.visitItem(item);
  }

  visitSimpleTableDoc(item: SimpleTableDoc): SimpleTableDoc {
    return this.visitItem(item);
  }
}

/**
 * This class coordinates re-evaluation and persistence of all noteRuntimeSnapshots contained within a
 * "top-level document" (TRoot, e.g WorkflowEntryTemplate, Entry, Protocol) which wraps a known
 * "hierarchical document" (TDoc, e.g. TemplateVersion, EntryVersion, ProtocolVersion).
 *
 * A subclass must be implemented for each "top-level document" type.
 */
export default abstract class DocumentEvaluationUpdater<TRoot, TDoc> {
  private readonly multidocTableOp?: MultidocTableOpMap;
  private evalMetrics?: EvaluationMetrics;

  abstract traverse(evalTraverser: DocumentTraverser, newDoc: TDoc, oldDoc: TDoc | null): void;
  abstract mapDoc(mapper: DocumentMapper, doc: TDoc): TDoc;
  abstract getDoc(root: TRoot): TDoc;
  abstract updatedRoot(root: TRoot, doc: TDoc): TRoot;

  /**
   * Override this method if the root contains documents that need name-to-ID mapping.
   */
  getNameIdMapFromRoot(root: TRoot): {[documentName: string]: Array<string>} {
    return {};
  }

  constructor(
    private readonly fileId: string,
    private readonly getRoot: () => TRoot | null,
    private readonly onAsyncUpdateComplete: (
      newDoc: TDoc,
      asyncMultidocTableOp: MultidocTableOpMap,
      evalMetrics?: EvaluationMetrics
    ) => void,
    {
      multidocTableOp,
      evalMetrics,
    }: {
      multidocTableOp?: MultidocTableOpMap;
      evalMetrics?: EvaluationMetrics;
    } = {}
  ) {
    this.multidocTableOp = multidocTableOp;
    this.evalMetrics = evalMetrics;
  }

  _getSyncUpdates(tableEvals: Array<TableEvaluation>): Array<EvalMergeTuple> {
    return tableEvals.map((syncEval) => ({
      cid: syncEval.cid,
      mergeUpdateInto(noteRuntimeSnapshot: NoteRuntimeSnapshot): NoteRuntimeSnapshot {
        return {
          ...noteRuntimeSnapshot,
          evaluationSnapshot: syncEval.evaluationSnapshot,
          excessVersion: excess.EXCESS_VERSION,
          queryResolutions: syncEval.syncResolutions,
        };
      },
    }));
  }

  async _waitForResolutionUpdates(syncEval: TableEvaluation): Promise<EvalMergeTuple | null> {
    const newQueryResolutions = await Promise.all(syncEval.asyncResolutions);
    if (newQueryResolutions.length === 0) {
      return null;
    }
    return {
      cid: syncEval.cid,
      mergeUpdateInto(noteRuntimeSnapshot: NoteRuntimeSnapshot): NoteRuntimeSnapshot {
        return {
          ...noteRuntimeSnapshot,
          queryResolutions: mergeResolutions(noteRuntimeSnapshot.queryResolutions, newQueryResolutions),
        };
      },
    };
  }

  _logQuiescenceMetrics(): void {
    if (!this.evalMetrics) {
      return;
    }

    const latency = performance.now() - this.evalMetrics.startTime;
    distribution('deu_quiescence_latency', latency, {
      deu_quiescence_cycles: this.evalMetrics.cycles,
    });

    distribution('deu_quiescence_cycles', this.evalMetrics.cycles);
  }

  _recordEvalMetrics(): void {
    if (!this.evalMetrics) {
      this.evalMetrics = {
        startTime: performance.now(),
        cycles: 1,
      };
    } else {
      this.evalMetrics.cycles++;
    }
  }

  async _runAsyncUpdates(tableEvals: Array<TableEvaluation>): Promise<void> {
    const tupleOrNulls = await Promise.all(
      tableEvals.map((syncEval) => this._waitForResolutionUpdates(syncEval))
    );

    const resMergeTuples = tupleOrNulls.filter((tupleOrNull) => tupleOrNull != null);
    if (!resMergeTuples.length) {
      this._logQuiescenceMetrics();
      return;
    }
    const root = this.getRoot();
    if (!root) {
      notifications.error('Cannot resolve loading cells when entry is not editable.');
      return;
    }

    // TODO(BNCH-14583): In the bootstrap case, currently all subclasses of DocumentEvaluationUpdater will
    // specify {bumpVersion: false} when performing async resolutions, which basically will silently update
    // the entry without persisting it. This is bad. We should consider either:
    //   a. blocking edits until bootstrapping finishes (arguably good UX but more complex implementation)
    //   b. cancel subsequent bootstrap updates if there has been an edit (bad UX, easy implementation)
    //   c. bump the version / attribute the resolution to the user editing the entry
    // See EntryStore-test.ts: 'handle async bootstrap (withInterimUpdate=true)
    const liveDoc = this.getDoc(root);
    const resMapper = new DocumentMapper(new EvalMergeVisitor(resMergeTuples));
    const liveDocWithEval = this.mapDoc(resMapper, liveDoc);
    const multidocTableOp = this._multidocTableOpFromTableEvals(tableEvals);
    this.onAsyncUpdateComplete(liveDocWithEval, multidocTableOp, this.evalMetrics);
  }

  _multidocTableOpFromTableEvals(tableEvals: Array<TableEvaluation>): MultidocTableOpMap {
    const result: MultidocTableOpMap = {};
    tableEvals.forEach((tableEval) => {
      if (tableEval.cellsWithUnresolvedResolutionQueries.size === 0) {
        return;
      }
      tableEval.cellsWithUnresolvedResolutionQueries.forEach((cellHash) => {
        const cellId = revhashCellId(cellHash);
        let tableOp = result[cellId.documentId];
        if (tableOp == null) {
          tableOp = {};
          result[cellId.documentId] = tableOp;
        }
        if (isTableOpDiffMap(tableOp)) {
          if (tableOp[cellId.tableName] == null) {
            tableOp[cellId.tableName] = {type: 'update-cells', cells: []};
          }
          (tableOp[cellId.tableName] as UpdateCellsTableOp).cells.push({
            row: cellId.row,
            column: cellId.column,
          });
        }
      });
    });
    return result;
  }

  _runOnDoc(
    newDoc: TDoc,
    oldDoc: TDoc | null,
    documentNameToIds: {[documentName: string]: Array<string>} = {}
  ): TDoc {
    // From the source, re-evaluate noteRuntimeSnapshot.evaluationTables. Surfacing oldDoc allows us to
    // skip evaluation when it hasn't changed.

    const visitor = new EvaluationVisitor(this.fileId, documentNameToIds, this.multidocTableOp, () => {
      this._recordEvalMetrics();
    });
    const evalTraverser = new DocumentTraverser(visitor);
    this.traverse(evalTraverser, newDoc, oldDoc);
    const evalMergeTuples = this._getSyncUpdates(visitor.tableEvals);

    // Kick off any async re-evals.
    this._runAsyncUpdates(visitor.tableEvals);

    // Return the entry with synchronous edits applied
    const mapper = new DocumentMapper(new EvalMergeVisitor(evalMergeTuples));
    const newDocVersion = this.mapDoc(mapper, newDoc);

    return newDocVersion;
  }
  run(root: TRoot, oldRoot: TRoot | null): TRoot {
    const doc = this.getDoc(root);
    const oldDoc = oldRoot ? this.getDoc(oldRoot) : null;

    const documentNameToIds = this.getNameIdMapFromRoot(root);

    const newDoc = this._runOnDoc(doc, oldDoc, documentNameToIds);
    return this.updatedRoot(root, newDoc);
  }
}
